/**
 * The Apps tab's Git for one service (its repo, `services/<name>`): the current branch, what changed here, ahead/behind
 * its upstream and the last commit; every remote with its URL and how the branch stands there (gitModel remoteStatusOf:
 * the overview compares with the upstream only). Actions as the project's Git tab (components/git/GitTab.jsx), on this
 * repo: Commit, Switch branch (local and remote ones; a remote one makes a tracking branch), New branch, Fetch all, and
 * Pull, Push and Fetch per remote; Remotes… to add or change one. "Open in Git tab" opens the full tab.
 *
 * Same guards as the Git tab: `readOnly` (a viewer) reads only, no action; a repo flagged Read-only (utils/repoFlags.js)
 * keeps Fetch, Pull and Switch branch, and says why Commit, Push and New branch are off. Read again after each action,
 * when the page is back, and on the project's tree events. Data: services/GitService.js (one overview for the project,
 * this service's repo picked out of it).
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown } from 'antd';
import { CaretDown, GitBranch } from '@phosphor-icons/react';
import config from '../../../config';
import * as git from '../../../services/GitService';
import { connectProjectTreeSyncRaw } from '../../../utils/vfsSync';
import useRepoFlags from '../../../hooks/useRepoFlags';
import { BranchModal, CommitModal, GitErrorText, RemotesModal } from '../../git/GitDialogs';
import { resultText, withPushHint } from '../../git/GitTab';
import {
  changedCount, lastCommitText, remoteBranchesOf, remoteStatusOf, remotesOf,
} from '../../git/gitModel';
import { Card, OpenLink } from './common';
import '../../git/Git.css';
import './GitTab.css';

const VERBS = { pull: 'Pulled from', push: 'Pushed to', fetch: 'Fetched branches and tags of' };

/** The overview's repo of `service` (`api` or `services/api`). */
const repoOf = (repos, service) => {
  const short = String(service || '').replace(/^services\//, '');
  return (repos || []).find((repo) => String(repo.service || '').replace(/^services\//, '') === short) || null;
};

export default function GitTab({ projectId, service, onOpen = null, readOnly = false }) {
  const canWrite = !readOnly;
  const [state, setState] = useState({ loading: true, error: null, repo: null, cloneBase: null });
  // `running`: the action in progress (`pull:origin`, `checkout`…); `notice`: what the last one answered.
  const [running, setRunning] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null);
  const { serviceFlags } = useRepoFlags(projectId);
  const flags = serviceFlags(service);
  const locked = flags?.readOnly ? `${flags.repo} is read-only in this project: only Fetch, Pull and Switch branch.` : null;

  const load = useCallback(async() => {
    try {
      const data = await git.getOverview(projectId);
      setState({ loading: false, error: null, repo: repoOf(data.repos, service), cloneBase: data.flow?.cloneBase || null });
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false, error }));
    }
  }, [projectId, service]);

  useEffect(() => {
    setState({ loading: true, error: null, repo: null, cloneBase: null });
    setNotice(null);
    load();
  }, [load]);

  // Files saved, a terminal command, a push from a clone: read again shortly after the last one (as the Git tab).
  useEffect(() => {
    if (!config.vfsServiceUrl || !projectId) { return undefined; }
    let timer = null;
    const soon = () => { clearTimeout(timer); timer = setTimeout(load, 800); };
    const disconnect = connectProjectTreeSyncRaw(config.vfsServiceUrl, projectId, {
      onFullResync: (type) => { if (type !== 'tree:init') { soon(); } },
      onPathChanged: soon,
      onPathDeleted: soon,
      onPathMoved: soon,
      onGitEvent: soon,
    });
    return () => { clearTimeout(timer); disconnect(); };
  }, [projectId, load]);

  useEffect(() => {
    const again = () => { if (document.visibilityState !== 'hidden') { load(); } };
    window.addEventListener('focus', again);
    return () => window.removeEventListener('focus', again);
  }, [load]);

  const { repo } = state;
  const name = repo?.service || service;

  /** One action on this repo, its notice after, then the overview again. */
  const act = async(kind, args = {}) => {
    setRunning(args.remotes?.length === 1 ? `${kind}:${args.remotes[0]}` : kind);
    setNotice(null);
    try {
      if (kind === 'checkout') {
        const answer = await git.checkout(projectId, { service: name, branch: args.branch });
        setNotice({ kind: 'ok', text: args.remote ? `Switched to ${answer?.branch || args.branch}, tracking ${args.branch}.` : `Switched to ${args.branch}.` });
      } else {
        const run = { pull: git.pullAll, push: git.pushAll, fetch: git.fetchAll }[kind];
        const results = await run(projectId, { service: name, remotes: args.remotes, ...(kind === 'fetch' ? { tags: true } : {}) });
        setNotice(resultText(VERBS[kind], results));
      }
    } catch (error) {
      setNotice({ kind: 'error', error: kind === 'push' ? withPushHint(error) : error });
    } finally {
      setRunning(null);
    }
    await load();
  };

  const onCommit = async(message) => {
    const answer = await git.commit(projectId, { service: name, message });
    const first = Array.isArray(answer?.shas) ? answer.shas[0] : null;
    const sha = first ? ` ${String(typeof first === 'object' ? first.sha || '' : first).slice(0, 7)}` : '';
    setNotice({ kind: 'ok', text: `Committed${sha}.` });
    load();
  };
  const onBranch = async(branch) => {
    await git.createBranch(projectId, { service: name, name: branch });
    setNotice({ kind: 'ok', text: `On the new branch ${branch}.` });
    load();
  };

  const body = () => {
    if (state.loading && !repo) { return <p className="apps-muted" role="status">Reading the repository…</p>; }
    if (state.error && !repo) {
      return (
        <Card testId="apps-git-error" title="Git" wide>
          <GitErrorText error={state.error} />
          <p className="apps-git-again"><Button onClick={load}>Try again</Button></p>
        </Card>
      );
    }
    if (!repo) { return <Card testId="apps-git-none" title="Git" wide><p className="apps-muted">No repository for this service yet.</p></Card>; }
    const busy = Boolean(running);
    const count = changedCount(repo);
    const remotes = remotesOf(repo);
    const last = lastCommitText(repo);
    const branches = Array.isArray(repo.branches) && repo.branches.length ? repo.branches : [repo.branch].filter(Boolean);
    const remoteBranches = remoteBranchesOf(repo);
    const switchMenu = {
      items: [
        { type: 'group', label: 'Here', children: branches.map((b) => ({ key: `b:${b}`, label: b, disabled: b === repo.branch })) },
        ...(remoteBranches.length ? [{
          type: 'group',
          label: 'On the remotes',
          children: remoteBranches.map((b) => ({ key: `r:${b.ref}`, label: <span data-testid={`apps-git-remote-branch-${b.ref}`}>{b.ref}</span> })),
        }] : []),
      ],
      onClick: ({ key }) => act('checkout', { branch: key.slice(2), remote: key.startsWith('r:') }),
    };
    return (
      <>
        <Card testId="apps-git-local" title="This repository" wide>
          <div className="apps-git-line">
            <span className="git-chip is-branch" data-testid="apps-git-branch"><GitBranch aria-hidden size={13} />{repo.branch || 'detached'}</span>
            {repo.initialized === false && <span className="git-chip is-changed">no repository yet</span>}
            {Number(repo.conflicted) > 0 && <span className="git-chip is-conflict" data-testid="apps-git-conflicts">{`${repo.conflicted} ${Number(repo.conflicted) === 1 ? 'conflict' : 'conflicts'}`}</span>}
            {count > 0
              ? <span className="git-chip is-changed" data-testid="apps-git-changed"><span aria-hidden className="git-dot" />{`${count} changed ${count === 1 ? 'file' : 'files'}`}</span>
              : <span className="git-chip is-clean" data-testid="apps-git-clean">clean</span>}
            <span className="git-sync" data-testid="apps-git-sync" title={repo.upstream ? `Compared with ${repo.upstream}` : undefined}>
              {remotes.length === 0 && 'Hosted by Flow'}
              {remotes.length > 0 && (repo.upstream ? `↑${Number(repo.ahead) || 0} ↓${Number(repo.behind) || 0} vs ${repo.upstream}` : 'no upstream yet')}
            </span>
          </div>
          <p className="apps-git-last" data-testid="apps-git-last">
            <span className="apps-muted">Last commit </span>
            {last || 'none yet'}
            {repo.lastCommit?.sha && <code className="apps-git-sha">{String(repo.lastCommit.sha).slice(0, 7)}</code>}
          </p>
          {canWrite && (
            <div className="apps-git-actions">
              <Button data-testid="apps-git-commit" disabled={busy || count === 0 || Boolean(locked)} onClick={() => setDialog('commit')} title={locked || undefined}>Commit</Button>
              <Dropdown disabled={busy} menu={switchMenu} trigger={['click']}>
                <Button data-testid="apps-git-switch" loading={running === 'checkout'}>Switch branch <CaretDown aria-hidden size={12} /></Button>
              </Dropdown>
              <Button data-testid="apps-git-new-branch" disabled={busy || Boolean(locked)} onClick={() => setDialog('branch')} title={locked || undefined}>New branch</Button>
              {remotes.length > 1 && <Button data-testid="apps-git-fetch-all" disabled={busy} loading={running === 'fetch'} onClick={() => act('fetch', { remotes: remotes.map((r) => r.name) })}>Fetch all</Button>}
            </div>
          )}
        </Card>
        <Card link="Remotes…" onLink={() => setDialog('remotes')} testId="apps-git-remotes" title={`Remotes · ${remotes.length}`} wide>
          {remotes.length === 0 && (
            <p className="apps-muted">None: Flow hosts this repository.{canWrite ? ' Add one to push it to your git server.' : ''}</p>
          )}
          {remotes.length > 0 && (
            <ul className="apps-git-remotes">
              {remotes.map((remote) => {
                const status = remoteStatusOf(repo, remote);
                const on = (verb) => running === `${verb}:${remote.name}`;
                return (
                  <li className="apps-git-remote" data-testid={`apps-git-remote-${remote.name}`} key={remote.name}>
                    <div className="apps-git-remote-head">
                      <span className="apps-git-remote-name">{remote.name}</span>
                      <span className={`git-sync apps-git-status is-${status.kind}`} data-testid={`apps-git-status-${remote.name}`} title={status.title}>{status.text}</span>
                    </div>
                    <code className="apps-git-url" title={remote.url}>{remote.url}</code>
                    {canWrite && (
                      <div className="apps-git-actions">
                        <Button data-testid={`apps-git-pull-${remote.name}`} disabled={busy} loading={on('pull')} onClick={() => act('pull', { remotes: [remote.name] })} size="small">Pull</Button>
                        <Button data-testid={`apps-git-push-${remote.name}`} disabled={busy || Boolean(locked)} loading={on('push')} onClick={() => act('push', { remotes: [remote.name] })} size="small" title={locked || undefined}>Push</Button>
                        <Button data-testid={`apps-git-fetch-${remote.name}`} disabled={busy} loading={on('fetch')} onClick={() => act('fetch', { remotes: [remote.name] })} size="small">Fetch</Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </>
    );
  };

  return (
    <div className="apps-tab-body apps-git" data-testid="apps-tabpanel-git">
      {readOnly && <p className="git-banner is-info">You can look: committing, pushing and pulling are for the project&apos;s owner and editors.</p>}
      {canWrite && locked && <p className="git-banner is-warn" data-testid="apps-git-locked">{locked}</p>}
      {notice?.kind === 'ok' && <p className="git-note" data-testid="apps-git-notice" role="status">{notice.text}</p>}
      {notice?.kind === 'error' && (
        <div data-testid="apps-git-notice">
          {notice.text && <p className="git-note" role="status">{notice.text}</p>}
          <GitErrorText error={notice.error} />
        </div>
      )}
      <div className="apps-infos">{body()}</div>
      <OpenLink label="Open in Git tab" onOpen={onOpen} testId="apps-open-git" />
      {repo && dialog === 'commit' && canWrite && !locked && <CommitModal onCancel={() => setDialog(null)} onCommit={onCommit} repo={repo} />}
      {repo && dialog === 'branch' && canWrite && !locked && <BranchModal onCancel={() => setDialog(null)} onCreate={onBranch} repo={repo} />}
      {repo && dialog === 'remotes' && (
        <RemotesModal canWrite={canWrite} cloneBase={state.cloneBase} onCancel={() => setDialog(null)} onChanged={load} projectId={projectId} repo={repo} />
      )}
    </div>
  );
}

GitTab.propTypes = {
  onOpen: PropTypes.func,
  projectId: PropTypes.string,
  // A viewer: the status only, no write action.
  readOnly: PropTypes.bool,
  service: PropTypes.string.isRequired,
};
